import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import StyledWrapper from '../style';
import { Alert, Box, Button, CircularProgress, Collapse, IconButton } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AppForm from '../components/AppForm/AppForm';
import ReviewForm from '../components/ReviewForm/ReviewForm';
import Instructions from '../../../components/FormBuilder/customComponents/Instructions';
import { setPencilIcon, validateRequiredFields } from '../../../utils/utilFunctions';
import { ACCOUNT_SOURCE, getLocalAuth } from '../../../utils/environmentUtils';
import {
	useGetUniqueFormQuery,
	useGetFormInstanceSchemaQuery,
	useLazyGetReviewAverageScoreQuery
} from '../../../services/endpoints/formBuilderEndpoints';
import { useUpdateFormStatusMutation } from '../../../services/endpoints/formBuilderEndpoints';
import {
	clearFormSchema,
	setToReviewForms,
	setFormSchema,
	setFormData,
	clearValues
} from '../../../components/FormBuilderLibrary/slice';
import { disabledStatuses } from '../../../constants/statuses';
import usePermissions from '../../../hooks/usePermissions';
import { checkInRange, useIndividualApplicationData } from '../../IndividualApplication/utils';
import { exportFormToPDF } from '../../../utils/utilFunctions';
import { COMPLETED } from '../../../constants/common';
import useIsFormInCurrentStage from '../useIsFormInCurrentStage';
import { clearFormFieldDataBackup } from '../../../utils/formBackupUtils';
import { useUnsavedChanges } from '../../../components/UnsavedChangesModal/useUnsavedChanges';
import UnsavedChangesModal from '../../../components/UnsavedChangesModal/UnsavedChangesModal';
import RestoreBackupModal from '../../../components/RestoreBackupModal/RestoreBackupModal';
import { useRestoreBackupModal } from '../../../components/RestoreBackupModal/useRestoreBackupModal';

const ReviewDetails = ({ isMasterLayout, navData, formID, isFormsContainer }) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const [updateFormStatus] = useUpdateFormStatusMutation();
	const [getReviewAverageScore] = useLazyGetReviewAverageScoreQuery();

	const { showWarning, handleConfirmNavigation, handleCancelNavigation, onCancel } =
		useUnsavedChanges(formID);

	const [alertBalloon, setAlertBalloon] = useState(false);
	const [readOnly, setReadOnly] = useState(location?.state?.isEditing ? false : true);
	const [loadingCancel, setLoadingCancel] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [isWithinRange, setIsWithinRange] = useState(false);
	const [completionErrors, setCompletionErrors] = useState([]);

	const handleRestoreData = useCallback(
		(fieldData) => {
			dispatch(setFormData([1, fieldData]));
		},
		[dispatch]
	);

	const { showRestoreModal, handleRestoreBackup, handleDeleteBackup, formKey } =
		useRestoreBackupModal({
			formID,
			loadingCancel,
			onRestore: handleRestoreData
		});

	const { hasEditPermissions, userAccountTypeID } = usePermissions();
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const localAuth = getLocalAuth(accountSource);
	const { access_token: apiToken, accountSource: localAccountSource } = localAuth || {};
	const pathArray = location?.pathname?.split('/');
	const masterForm = pathArray?.filter((e) => e?.includes('rfm-'))?.[0];
	const reviewForm = pathArray?.filter((e) => e?.includes('rfi-'))?.[0];
	let subPage = reviewForm || masterForm || '';
	const appSegment = pathArray?.find((e) => e?.includes('api-')) || '';
	const { data: application } = useIndividualApplicationData();
	const applicationStatus = application?.status;
	const normalizedApplicationStatus = (applicationStatus ?? '').toString().toLowerCase();
	const isApplicationRejected =
		normalizedApplicationStatus.includes('reject') ||
		normalizedApplicationStatus.includes('denied');
	const matchesDisabledStatus = disabledStatuses.some(
		(status) => status?.toString().toLowerCase() === normalizedApplicationStatus
	);
	const shouldHideFormActions = isApplicationRejected || matchesDisabledStatus;

	const uniqueID =
		subPage && subPage?.replace('rfm-', '')?.replace('-group', '')?.replace('rfi-', '');

	const isFormInCurrentStage = useIsFormInCurrentStage(application, uniqueID);

	const uniqueForm = useGetUniqueFormQuery(
		{
			uniqueID,
			apiToken
		},
		{ skip: !uniqueID || !isMasterLayout }
	);

	const formInstanceSchema = useGetFormInstanceSchemaQuery(
		{
			uniqueID,
			apiToken
		},
		{ skip: !uniqueID || isMasterLayout }
	);

	const { data, isLoading } = isMasterLayout ? uniqueForm : formInstanceSchema;

	useEffect(() => {
		setLoadingCancel(true);
		dispatch(clearFormSchema());
		dispatch(setToReviewForms([]));
		dispatch(clearValues());
		setReadOnly(location?.state?.isEditing ? false : true);
	}, [location.pathname, formID]);

	const instructions = isMasterLayout ? data?.instructions : data?.form?.instructions;

	useEffect(() => {
		const isReturned = navData?.[0]?.status?.includes('returned');
		const isReopened = data?.status?.includes('reopened');
		const overrideDates = isReturned || isReopened;
		if (data && isMasterLayout) {
			const isWithinDateRange = checkInRange(application, data?.form?.stage, overrideDates);
			setIsWithinRange(isWithinDateRange);
			dispatch(setFormSchema([0, [...(data?.form_schema ?? [])]]));
			dispatch(setToReviewForms(data?.stage?.review_info));
		} else if (data) {
			const isWithinDateRange = checkInRange(application, data?.form?.stage, overrideDates);
			setIsWithinRange(isWithinDateRange);

			dispatch(setFormSchema([0, [...(data?.form_schema ?? [])]]));
			dispatch(setFormData([1, data?.field_values || []]));
			dispatch(setToReviewForms(data?.form?.stage?.review_info));
		}
		setCompletionErrors([]);
		setTimeout(function () {
			setLoadingCancel(false);
		}, 1000);
	}, [application, data, dispatch, isLoading, isMasterLayout, navData, formID]);

	useEffect(() => {
		setPencilIcon(readOnly);
	}, [readOnly]);

	const handleAlert = (message, status) => {
		setAlertBalloon({ isOpen: true, message, status });
		setTimeout(() => {
			setAlertBalloon(false);
		}, 3000);
	};

	const handleExportToPDF = async () => {
		let averageScore;
		try {
			const result = await getReviewAverageScore({
				uniqueID,
				apiToken
			}).unwrap();
			averageScore = result.average_score;
		} catch {
			handleAlert('Unable to calculate the average review score. PDF export failed.', 'error');
			return;
		}

		try {
			await exportFormToPDF(
				{
					...data,
					applicationTitle: application?.project_title,
					applicationUniqueID: appSegment
				},
				null,
				null,
				false,
				averageScore
			);
		} catch {
			handleAlert('Unable to generate the PDF. Please try again.', 'error');
		}
	};

	const canEditForm = useMemo(() => {
		const isReturnedOrReopened =
			isWithinRange ||
			navData?.[0]?.status?.toLowerCase()?.includes('returned') ||
			data?.status?.toLowerCase()?.includes('reopened');

		const isNotStarted =
			data?.status?.toLowerCase()?.includes('not started') ||
			data?.status?.toLowerCase()?.includes('draft');

		const isNotCompleted = !data?.status?.toLowerCase()?.includes('completed');

		const userHasRights = userAccountTypeID !== 5;

		return (
			isReturnedOrReopened &&
			isNotStarted &&
			isNotCompleted &&
			isFormInCurrentStage &&
			userHasRights &&
			!shouldHideFormActions
		);
	}, [
		isWithinRange,
		navData,
		data?.status,
		userAccountTypeID,
		isFormInCurrentStage,
		shouldHideFormActions
	]);

	useEffect(() => {
		if (shouldHideFormActions) {
			setReadOnly(true);
		}
	}, [shouldHideFormActions]);

	const handleComplete = () => {
		const completeValidationErrors = validateRequiredFields(data.form_schema, data.field_values);
		if (completeValidationErrors.length === 0) {
			setButtonLoading('complete');
			let id = formID?.split('-')?.[1];
			const options = {
				id: id,
				status: 'Completed',
				apiToken: apiToken
			};
			updateFormStatus(options)
				.unwrap()
				.then(async () => {
					setButtonLoading(false);
					setCompletionErrors([]);
					handleAlert('Status Updated Successfully!', 'success');
				})
				.catch(() => {
					setButtonLoading(false);
					handleAlert('Status Update Failed!', 'error');
				});
		} else {
			setButtonLoading(``);
			setCompletionErrors(completeValidationErrors);
		}
	};
	const handleCancel = () => {
		onCancel(() => {
			setLoadingCancel(true);
			dispatch(setFormData([1, data?.field_values || []]));
			setReadOnly(true);
			setCompletionErrors([]);
			setTimeout(function () {
				setLoadingCancel(false);
			}, 1000);

			if (formID) {
				clearFormFieldDataBackup(formID);
			}
		});
	};

	let beforeDeadline = true;
	if (data) {
		if (data?.status?.includes('reopened')) {
			beforeDeadline = true;
		} else {
			const endDateRaw =
				data?.form?.stage?.end_date?.trim() || data?.form?.stage?.program?.end_date;
			if (endDateRaw) {
				beforeDeadline = new Date(endDateRaw) > new Date();
			} else {
				beforeDeadline = true;
			}
		}
	}

	return (
		<StyledWrapper.OutermostContainer>
			{!beforeDeadline && data?.status != 'Completed' && (
				<Alert variant="outlined" severity="error">
					The deadline has passed for this program. This application can no longer be submitted.
				</Alert>
			)}
			<Instructions data={{ content: instructions }} />
			<Collapse in={alertBalloon}>
				<Alert
					severity={alertBalloon?.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status}
							size="small"
							onClick={() => {
								setAlertBalloon(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</Alert>
			</Collapse>

			<StyledWrapper.FormHeader>
				{(hasEditPermissions || !userAccountTypeID) && (
					<StyledWrapper.LastEditedActions>
						{readOnly ? (
							<>
								{!isFormsContainer && isWithinRange && (
									<>
										{data && data.status.toLowerCase() !== 'not started' && (
											<Button variant="contained" onClick={handleExportToPDF}>
												Export to PDF
											</Button>
										)}
										{canEditForm && (
											<Button
												data-testid={'individualreview-edit-button'}
												variant="outlined"
												onClick={() => setReadOnly(false)}
												startIcon={<EditIcon />}
											>
												Edit Review
											</Button>
										)}

										{beforeDeadline &&
											data.status.toLowerCase().includes('draft') &&
											!shouldHideFormActions && (
												<LoadingButton
													loading={buttonLoading === 'complete'}
													variant="contained"
													component="label"
													data-testid={'individualapp-save-button'}
													onClick={handleComplete}
												>
													Complete
												</LoadingButton>
											)}
										{localAccountSource === ACCOUNT_SOURCE.GRANTS_NETWORK &&
											data.status.toLowerCase().includes(COMPLETED) &&
											!matchesDisabledStatus &&
											!shouldHideFormActions && (
												<LoadingButton
													loading={buttonLoading === 'reopen'}
													variant="contained"
													component="label"
													data-testid={'individualapp-save-button'}
													onClick={() => {
														setButtonLoading('reopen');
														let id = formID?.split('-')?.[1];
														const options = {
															id: id,
															status: 'Draft (returned)',
															apiToken: apiToken
														};
														updateFormStatus(options)
															.unwrap()
															.then(async () => {
																setButtonLoading(false);
																handleAlert('Status Updated Successfully!', 'success');
															})
															.catch(() => {
																setButtonLoading(false);
																handleAlert('Status Update Failed!', 'error');
															});
													}}
												>
													Reopen
												</LoadingButton>
											)}
									</>
								)}
							</>
						) : (
							isWithinRange && (
								<>
									{data && data.status.toLowerCase() !== 'not started' && (
										<Button variant="contained" onClick={handleExportToPDF}>
											Export to PDF
										</Button>
									)}

									{!shouldHideFormActions && (
										<>
											<Button
												variant="contained"
												component="label"
												data-testid={'individualreview-save-button'}
												onClick={() => {
													setCompletionErrors([]);
													const element = document.getElementById(`${formID}-saveButton`);
													element.click();
												}}
											>
												Save
											</Button>
											<Button
												variant="outlined"
												onClick={handleCancel}
												data-testid={'individualreview-cancel-button'}
											>
												Cancel
											</Button>
										</>
									)}
								</>
							)
						)}
					</StyledWrapper.LastEditedActions>
				)}
			</StyledWrapper.FormHeader>
			{loadingCancel || isLoading ? (
				<>
					<Box
						sx={{
							display: 'flex',
							width: '100%',
							height: '100%',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					>
						<CircularProgress />
					</Box>
				</>
			) : (
				<StyledWrapper.InnerContainer>
					<Box sx={{ flex: '1 1 0', minWidth: 0 }}>
						<AppForm isMasterLayout={isMasterLayout} />
					</Box>
					<Box sx={{ flex: '1 1 0', minWidth: 0 }}>
						<ReviewForm
							key={`${JSON.stringify(data)}-${formKey}`}
							apiFormData={data}
							isMasterLayout={isMasterLayout}
							readOnly={isMasterLayout || readOnly}
							setReadOnly={setReadOnly}
							formID={formID}
							disableActionButtons
							staticInstructions={instructions}
							completionErrors={completionErrors}
							applicationStatus={applicationStatus}
						/>
					</Box>
					<UnsavedChangesModal
						handleCancelNavigation={handleCancelNavigation}
						showWarning={showWarning}
						handleConfirmNavigation={handleConfirmNavigation}
					/>
					<RestoreBackupModal
						showRestoreModal={showRestoreModal}
						handleRestoreBackup={handleRestoreBackup}
						handleDeleteBackup={handleDeleteBackup}
					/>
				</StyledWrapper.InnerContainer>
			)}
		</StyledWrapper.OutermostContainer>
	);
};

export default ReviewDetails;
